// Base-map layers: optional raster tiles, DEM-derived relief (hypsometric tint, multidirectional
// hillshade, contour lines) and OpenMapTiles vector features (water, forest, roads, places, peaks).
//
// The relief comes from the same terrain tiles the solver uses, so the "Terrain" map works offline
// once an area is cached. Vector features need network (or a self-hosted tile set later).

import type { ExpressionSpecification, LayerSpecification, SourceSpecification } from 'maplibre-gl';
import type { Basemap } from '../sources/types';
import type { AltUnit } from '../lib/units';

export const FONT = ['Noto Sans Regular'];
export const FONT_BOLD = ['Noto Sans Medium'];
export const FONT_ITALIC = ['Noto Sans Italic'];

/** Prefix of every base-map layer id, so switching base maps can remove them all. */
export const BASE = 'bm-';
/** Prefix of base-map label layers, which sit above the analysis overlays. */
export const LABEL = 'bm-label-';

const PAPER = '#f4f1ea';

/** Muted Swiss-style elevation tints that stay readable under the reach overlay. */
const TINT: (string | number)[] = [
  0, '#c8d9b0',
  300, '#d6e2bd',
  700, '#e4e7c6',
  1200, '#ece4c6',
  1700, '#e6d8bc',
  2200, '#dccfc0',
  2700, '#d9d6d2',
  3200, '#eeeeee',
  4000, '#ffffff',
];

/** Contour spacing per zoom, [minor, major]. */
export function contourThresholds(alt: AltUnit): Record<number, [number, number]> {
  return alt === 'ft'
    ? { 9: [1000, 5000], 10: [500, 2500], 11: [250, 1000], 12: [200, 1000], 13: [100, 500], 14: [50, 250] }
    : { 9: [500, 1000], 10: [200, 1000], 11: [100, 500], 12: [50, 250], 13: [20, 100], 14: [10, 50] };
}

const name: ExpressionSpecification = ['coalesce', ['get', 'name:latin'], ['get', 'name']];

export interface BaseLayers {
  sources: Record<string, SourceSpecification>;
  /** Drawn below the analysis overlays (paper, raster tiles, elevation tint). */
  below: LayerSpecification[];
  /** Drawn above the analysis overlays but below airspace and routes: hillshade (so relief
   * shows through the reach colours), map features, contours and labels. */
  above: LayerSpecification[];
}

/**
 * Layers for one base map. `contourTiles` is the tile URL of the contour vector source (it depends
 * on units), `shadeSource` the id of the raster-dem source used for tint and hillshade.
 */
export function baseLayers(b: Basemap, alt: AltUnit, opts: { shadeSource: string; contours: boolean }): BaseLayers {
  const sources: Record<string, SourceSpecification> = {};
  const below: LayerSpecification[] = [{ id: `${BASE}background`, type: 'background', paint: { 'background-color': PAPER } }];
  const above: LayerSpecification[] = [];

  if (b.tiles?.length) {
    sources[`${BASE}raster`] = {
      type: 'raster',
      tiles: b.tiles,
      tileSize: b.tileSize ?? 256,
      maxzoom: b.maxzoom ?? 18,
      attribution: b.attribution,
    };
    below.push({ id: `${BASE}raster`, type: 'raster', source: `${BASE}raster`, paint: { 'raster-fade-duration': 100 } });
  }

  const r = b.relief;
  if (r?.tint) {
    below.push({
      id: `${BASE}tint`,
      type: 'color-relief',
      source: opts.shadeSource,
      paint: {
        'color-relief-opacity': 1,
        'color-relief-color': ['interpolate', ['linear'], ['elevation'], ...TINT] as never,
      },
    });
  }
  if (r && r.hillshade > 0) {
    above.push({
      id: `${BASE}hillshade`,
      type: 'hillshade',
      source: opts.shadeSource,
      paint: {
        'hillshade-method': 'multidirectional',
        // Main light from the north-west, softer fill lights, as on Swiss topographic maps.
        'hillshade-illumination-direction': [315, 270, 0, 225],
        'hillshade-illumination-altitude': [40, 35, 35, 25],
        'hillshade-shadow-color': [
          `rgba(60,48,40,${0.55 * r.hillshade})`,
          `rgba(60,48,40,${0.25 * r.hillshade})`,
          `rgba(60,48,40,${0.25 * r.hillshade})`,
          `rgba(40,40,70,${0.15 * r.hillshade})`,
        ],
        'hillshade-highlight-color': [
          `rgba(255,252,240,${0.45 * r.hillshade})`,
          'rgba(255,255,255,0)',
          'rgba(255,255,255,0)',
          'rgba(255,255,255,0)',
        ],
        'hillshade-exaggeration': 1,
      },
    } as LayerSpecification);
  }

  if (b.vector) {
    sources[`${BASE}omt`] = { type: 'vector', url: b.vector.url, attribution: b.vector.attribution };
    const src = { source: `${BASE}omt` };
    above.push(
      {
        id: `${BASE}ice`,
        type: 'fill',
        ...src,
        'source-layer': 'landcover',
        filter: ['==', ['get', 'class'], 'ice'],
        paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.75 },
      },
      {
        id: `${BASE}wood`,
        type: 'fill',
        ...src,
        'source-layer': 'landcover',
        filter: ['==', ['get', 'class'], 'wood'],
        paint: { 'fill-color': '#6f9a55', 'fill-opacity': ['interpolate', ['linear'], ['zoom'], 8, 0.12, 13, 0.22] },
      },
      {
        id: `${BASE}rock`,
        type: 'fill',
        ...src,
        'source-layer': 'landcover',
        filter: ['in', ['get', 'class'], ['literal', ['rock', 'sand']]],
        paint: { 'fill-color': '#d8d2c8', 'fill-opacity': 0.25 },
      },
      {
        id: `${BASE}water`,
        type: 'fill',
        ...src,
        'source-layer': 'water',
        paint: { 'fill-color': '#9fc5e3' },
      },
      {
        id: `${BASE}waterway`,
        type: 'line',
        ...src,
        'source-layer': 'waterway',
        minzoom: 9,
        paint: {
          'line-color': '#8bb8dc',
          'line-width': ['interpolate', ['linear'], ['zoom'], 9, ['match', ['get', 'class'], 'river', 1, 0.3], 14, ['match', ['get', 'class'], 'river', 3, 1]],
        },
      },
    );
  }

  if (opts.contours && r?.contours !== false) {
    above.push(
      {
        id: `${BASE}contour`,
        type: 'line',
        source: 'contours',
        'source-layer': 'contours',
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': b.tiles?.length && !b.relief?.tint ? 'rgba(255,240,210,0.55)' : 'rgba(120,82,45,0.45)',
          'line-width': ['match', ['get', 'level'], 1, 1.1, 0.55],
        },
      },
      {
        id: `${BASE}contour-label`,
        type: 'symbol',
        // A separate source, so that if a font range fails to load only the labels are lost.
        source: 'contour-labels',
        'source-layer': 'contours',
        filter: ['>', ['get', 'level'], 0],
        minzoom: 11,
        layout: {
          'symbol-placement': 'line',
          'text-field': ['concat', ['to-string', ['get', 'ele']], alt === 'ft' ? "'" : ''],
          'text-font': FONT,
          'text-size': 10,
          'symbol-spacing': 350,
          'text-max-angle': 25,
        },
        paint: {
          'text-color': b.tiles?.length && !b.relief?.tint ? '#fff6e6' : '#7a5530',
          'text-halo-color': b.tiles?.length && !b.relief?.tint ? 'rgba(0,0,0,0.5)' : 'rgba(244,241,234,0.9)',
          'text-halo-width': 1.2,
        },
      },
    );
  }

  if (b.vector) {
    const src = { source: `${BASE}omt`, 'source-layer': 'transportation' };
    const cls: ExpressionSpecification = ['get', 'class'];
    above.push(
      {
        id: `${BASE}path`,
        type: 'line',
        ...src,
        minzoom: 12,
        filter: ['in', cls, ['literal', ['path', 'track']]],
        paint: { 'line-color': '#8a5a35', 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.6, 16, 1.5], 'line-dasharray': [3, 2] },
      },
      {
        id: `${BASE}road-casing`,
        type: 'line',
        ...src,
        filter: ['in', cls, ['literal', ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor']]],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': 'rgba(120,110,95,0.6)',
          'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 8, ['match', cls, ['motorway', 'trunk'], 1.6, 'primary', 1.2, 0.4], 15, ['match', cls, ['motorway', 'trunk'], 9, 'primary', 8, 5]],
        },
      },
      {
        id: `${BASE}road`,
        type: 'line',
        ...src,
        filter: ['in', cls, ['literal', ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'minor']]],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['match', cls, ['motorway', 'trunk'], '#f3b05a', 'primary', '#f7d27a', '#ffffff'],
          'line-width': ['interpolate', ['exponential', 1.5], ['zoom'], 8, ['match', cls, ['motorway', 'trunk'], 1, 'primary', 0.7, 0], 15, ['match', cls, ['motorway', 'trunk'], 7, 'primary', 6, 3.5]],
        },
      },
      {
        id: `${BASE}cable`,
        type: 'line',
        ...src,
        minzoom: 11,
        filter: ['in', cls, ['literal', ['aerialway', 'cable_car', 'gondola']]],
        paint: { 'line-color': '#333', 'line-width': 1, 'line-dasharray': [6, 2] },
      },
    );
    const lsrc = { source: `${BASE}omt` };
    const halo = { 'text-halo-color': 'rgba(255,255,255,0.85)', 'text-halo-width': 1.4 };
    above.push(
      {
        id: `${LABEL}water`,
        type: 'symbol',
        ...lsrc,
        'source-layer': 'water_name',
        layout: { 'text-field': name, 'text-font': FONT_ITALIC, 'text-size': 12 },
        paint: { 'text-color': '#2f6690', ...halo },
      },
      {
        id: `${LABEL}peak`,
        type: 'symbol',
        ...lsrc,
        'source-layer': 'mountain_peak',
        minzoom: 10,
        filter: ['all', ['has', 'name'], ['in', ['get', 'class'], ['literal', ['peak', 'volcano']]]],
        layout: {
          'text-field': [
            'format',
            '▲ ', { 'text-color': '#6b4423' },
            name, {},
            '\n', {},
            ['concat', ['to-string', ['get', alt === 'ft' ? 'ele_ft' : 'ele']], alt === 'ft' ? ' ft' : ' m'],
            { 'font-scale': 0.85 },
          ],
          'text-font': FONT,
          'text-size': 11,
          'text-anchor': 'top',
          'text-offset': [0, -0.6],
          'symbol-sort-key': ['get', 'rank'],
        },
        paint: { 'text-color': '#4a3520', ...halo },
      },
      {
        id: `${LABEL}place`,
        type: 'symbol',
        ...lsrc,
        'source-layer': 'place',
        filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village', 'hamlet']]],
        layout: {
          'text-field': name,
          'text-font': ['match', ['get', 'class'], ['city', 'town'], ['literal', FONT_BOLD], ['literal', FONT]] as never,
          'text-size': ['match', ['get', 'class'], 'city', 15, 'town', 13, 'village', 11.5, 10.5],
          'symbol-sort-key': ['get', 'rank'],
        },
        minzoom: 7,
        paint: { 'text-color': '#222', ...halo },
      },
    );
  }

  return { sources, below, above };
}
